/**
 * Announcement Bar – Inline Countdown Timer
 *
 * Custom element: <announcement-countdown>
 *
 * Attributes
 *   data-countdown-type   "date" | "repeat" | "session"
 *   data-end-date         ISO-ish string, e.g. "2026-04-01 00:00"
 *   data-repeat-hours     Number of hours for repeating cycle
 *   data-session-hours    Number of hours counted down on every new visit
 *   data-session-key      Storage key, shared by timers that must stay in sync
 */
if (!customElements.get('announcement-countdown')) {
  class AnnouncementCountdown extends HTMLElement {
    constructor() {
      super();
      this.daysEl = this.querySelector('[data-cd-days]');
      this.daysPartEl = this.querySelector('[data-cd-days-part]');
      this.hoursEl = this.querySelector('[data-cd-hours]');
      this.minutesEl = this.querySelector('[data-cd-minutes]');
      this.secondsEl = this.querySelector('[data-cd-seconds]');
      this.type = this.getAttribute('data-countdown-type') || 'date';
      this.interval = null;
    }

    connectedCallback() {
      this.calculateEndDate();
      if (!this.endDate || isNaN(this.endDate)) return;
      this.update();
      this.interval = setInterval(() => this.update(), 1000);
    }

    disconnectedCallback() {
      if (this.interval) clearInterval(this.interval);
    }

    calculateEndDate() {
      if (this.type === 'date') {
        const raw = this.getAttribute('data-end-date');
        if (!raw) return;
        // Normalise "YYYY-MM-DD HH:MM" → "YYYY-MM-DDTHH:MM:00"
        this.endDate = Date.parse(raw.replace(' ', 'T') + (raw.includes(':') && raw.split(':').length < 3 ? ':00' : ''));
      } else if (this.type === 'repeat') {
        const hours = parseInt(this.getAttribute('data-repeat-hours'), 10) || 24;
        this.repeatMs = hours * 3600 * 1000;
        this.setRepeatEnd();
      } else if (this.type === 'session') {
        this.sessionHours = parseFloat(this.getAttribute('data-session-hours')) || 12;
        this.sessionKey = this.getAttribute('data-session-key') || 'campaign';
        this.setSessionEnd();
      }
    }

    /**
     * Per-visit mode: the timer starts over at its full length on every new
     * visit, and the shared helper keeps it ticking across page views.
     */
    setSessionEnd() {
      if (typeof window.sessionCountdownEnd === 'function') {
        this.endDate = window.sessionCountdownEnd(this.sessionKey, this.sessionHours);
      } else {
        this.endDate = Date.now() + this.sessionHours * 3600 * 1000;
      }
    }

    /**
     * For repeating mode we anchor to midnight of today and then find the
     * *next* cycle boundary from now.
     * Example: repeat = 6 h → cycles at 00:00, 06:00, 12:00, 18:00, 00:00…
     */
    setRepeatEnd() {
      const now = Date.now();
      // Anchor = start of today in visitor's local timezone
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const anchor = today.getTime();

      // How far into the cycles are we since anchor?
      const elapsed = (now - anchor) % this.repeatMs;
      this.endDate = now + (this.repeatMs - elapsed);
    }

    update() {
      let diff = this.endDate - Date.now();

      if (diff <= 0) {
        if (this.type === 'repeat' || this.type === 'session') {
          // Reset for the next cycle
          if (this.type === 'repeat') {
            this.setRepeatEnd();
          } else {
            this.setSessionEnd();
          }
          diff = this.endDate - Date.now();
        } else {
          // One-time countdown expired
          this.render(0, 0, 0, 0);
          clearInterval(this.interval);
          return;
        }
      }

      const totalSeconds = Math.floor(diff / 1000);
      const days = Math.floor(totalSeconds / 86400);
      const hours = Math.floor((totalSeconds % 86400) / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;

      this.render(days, hours, minutes, seconds);
    }

    pad(n) {
      return n < 10 ? '0' + n : String(n);
    }

    /**
     * Days are hidden entirely on the last day so the bar never reads
     * "0 gün 5 saat 03 dakika 22 saniye".
     */
    render(d, h, m, s) {
      if (this.daysEl) this.daysEl.textContent = d;
      if (this.daysPartEl) this.daysPartEl.hidden = d === 0;
      if (this.hoursEl) this.hoursEl.textContent = this.pad(h);
      if (this.minutesEl) this.minutesEl.textContent = this.pad(m);
      if (this.secondsEl) this.secondsEl.textContent = this.pad(s);
    }
  }

  customElements.define('announcement-countdown', AnnouncementCountdown);
}
